import type { TRTCConnection } from 'webrtc/types';

/**
 * AVS-4302 Task 10 (§5.6, §5.8): pure, state-free helpers that turn the raw remote
 * list into the customer-safe render list. Extracted out of the hook so the dedup and
 * empty-tile rules are unit-testable in isolation (business logic lives in utils, not
 * components/hooks). Both are gated by the caller on `isVoipSingleStreamEnabled` — when
 * the flag is off the hook never calls them, so the list is exactly today's.
 */

/** A "live" stream is a `MediaStream`; null / undefined / non-MediaStream is no stream. */
const hasLiveStream = (remote: TRTCConnection): boolean => remote.stream instanceof MediaStream;

/** The stable dealer identity for a remote, or `undefined` for a customer (no pseudonym). */
export const dealerPseudonymOf = (remote: Pick<TRTCConnection, 'meta'>): string | undefined => {
  const value = remote.meta?.dealerPseudonym;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

/**
 * AVS-4302: a "peer connected/disconnected" toast should announce only a genuinely different
 * party — the customer, or a DIFFERENT dealer. A same-dealer connection is THIS dealer's own
 * other tab (a second tab, or a takeover reconnect) — i.e. "me", not a peer — so its toast is
 * suppressed. Customers (no pseudonym) and different dealers are always announced; the check is
 * identity-scoped via the dealer pseudonym and applies only to a dealer viewer.
 */
export const isOwnDealerPeer = (
  peer: Pick<TRTCConnection, 'meta'> | undefined,
  myPseudonym: string | undefined,
  isDealer: boolean
): boolean => {
  if (!isDealer || !myPseudonym) return false;
  return dealerPseudonymOf(peer ?? {}) === myPseudonym;
};

/**
 * §5.6 — collapse remotes sharing the same `meta.dealerPseudonym` to ONE tile so the
 * customer never sees one dealer twice (takeover overlap, claim race, unmanaged
 * second-dealer duplicate, or a stale election-unaware client). Customers (no
 * pseudonym) are NEVER collapsed — each stays distinct. Distinct dealers stay distinct.
 *
 * Per-group winner, in order: (1) the active speaker, else (2) one with a live stream
 * (publishing master beats a no-stream standby), else (3) the first in input order
 * (stable). Output preserves the input order of the surviving remotes.
 */
export const dedupeRemotesByDealer = (remotes: TRTCConnection[]): TRTCConnection[] => {
  // Pick the winning connectionId per dealer first, then emit survivors in input order
  // so the result stays stable (no reordering of customers or distinct dealers).
  const winnerByDealer = new Map<string, TRTCConnection>();
  for (const remote of remotes) {
    const dealer = dealerPseudonymOf(remote);
    if (dealer === undefined) continue;
    const current = winnerByDealer.get(dealer);
    if (!current || beats(remote, current)) {
      winnerByDealer.set(dealer, remote);
    }
  }

  return remotes.filter((remote) => {
    const dealer = dealerPseudonymOf(remote);
    if (dealer === undefined) return true; // customers always survive
    return winnerByDealer.get(dealer) === remote;
  });
};

/** True when `candidate` should replace the `current` winner for the same dealer group. */
const beats = (candidate: TRTCConnection, current: TRTCConnection): boolean => {
  // (1) active speaker wins outright.
  if (!!candidate.isActiveSpeaker !== !!current.isActiveSpeaker) {
    return !!candidate.isActiveSpeaker;
  }
  if (candidate.isActiveSpeaker) return false; // both active — keep the first seen (stable)
  // (2) a live stream wins over a no-stream sibling.
  if (hasLiveStream(candidate) !== hasLiveStream(current)) {
    return hasLiveStream(candidate);
  }
  // (3) otherwise keep the first seen (stable) — candidate does not beat current.
  return false;
};

/**
 * §5.8 — exclude a remote ONLY IF it is a dealer (`meta.dealerPseudonym`) AND has no
 * live stream (a same-dealer standby / not-yet-publishing dealer, e.g. the master's
 * view of its own receive-only standby). A receive-only customer with no stream is
 * NEVER excluded — it still renders its placeholder tile.
 */
export const filterEmptyDealerTiles = (remotes: TRTCConnection[]): TRTCConnection[] =>
  remotes.filter((remote) => dealerPseudonymOf(remote) === undefined || hasLiveStream(remote));

/**
 * AVS-4302 takeover reliability — drop peers admitted from an inbound offer whose
 * `ConnectionAdded` (identity) has not arrived yet. They are fully negotiable, but without
 * `meta` every classifier above reads them as a customer: they would bypass the dealer dedup,
 * the own-tab exclusion, and could even win the active slot — a phantom tile. Render them only
 * once the Add merges their meta in. Non-pending customers (no meta, no flag) are untouched.
 */
export const filterIdentityPendingTiles = (remotes: TRTCConnection[]): TRTCConnection[] =>
  remotes.filter((remote) => !remote.metaPending);

/**
 * AVS-4311 — choose which single remote the minimized pill's "Them" tile promotes (also the
 * expanded panel's `activeConnectionId`). It must be the last-known speaker and, when no one
 * has spoken yet, a DETERMINISTIC pick — never the arbitrary object-insertion order (the
 * reported "shows 1 person randomly" bug), and never an id that has since left the call.
 *
 * Priority:
 *   1. the live active speaker (`isActiveSpeaker`), else the most-recent one
 *      (`lastActiveSpeaker`) — the promoted "last-known speaker". Live wins over stale even
 *      when the stale entry sits earlier in the list.
 *   2. otherwise keep the previous pick IFF it is still connected (avoids needless churn);
 *   3. otherwise a stable, order-independent pick: the lowest `connectionId`.
 * Returns '' when there are no remotes.
 */
export const resolveActiveConnectionId = (connections: TRTCConnection[], prevId: string): string => {
  if (connections.length === 0) return '';
  const speaker =
    connections.find((remote) => remote.isActiveSpeaker) ?? connections.find((remote) => remote.lastActiveSpeaker);
  if (speaker) return speaker.connectionId;
  if (prevId && connections.some((remote) => remote.connectionId === prevId)) return prevId;
  return connections.reduce((lowest, remote) => (remote.connectionId < lowest.connectionId ? remote : lowest))
    .connectionId;
};
